import React, { useState, useRef, useCallback, useEffect } from "react";
import TetherComponent from "react-tether";
import { useDispatch } from "react-redux";

import { useQueryClient } from "react-query";

import useOnClickOutside from "../hooks/on-click-outside";
import useAccountMetadata from "../hooks/useAccountMetadata";
import {
  fetchAccountMetadata,
  setCurrentEnvironment,
} from "../actions/environments";

import { EnvironmentDisplay } from "./EnvironmentDisplay";
import { EnvironmentPickerPanel } from "./EnvironmentPickerPanel";

interface NavigationEnvironmentPickerProps {
  collapsed?: boolean;
  className?: string;
}

export const NavigationEnvironmentPicker = ({
  collapsed = false,
  className,
}: NavigationEnvironmentPickerProps) => {
  const [open, setOpen] = useState(false);
  const { projects } = useAccountMetadata();
  const dispatch = useDispatch();
  const displayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  useOnClickOutside([displayRef, panelRef], () => {
    setOpen(false);
  });

  const setEnvironmentAndProjectFromPanel = useCallback(
    (environment: { ID: string | null }) => {
      if (environment.ID) {
        dispatch(setCurrentEnvironment(environment.ID, queryClient));
        setOpen(false);
      }
    },
    [dispatch, queryClient],
  );

  const toggleOpen = useCallback(() => {
    setOpen(!open);
  }, [open, setOpen]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    dispatch(fetchAccountMetadata());
  }, [dispatch]);

  return (
    <TetherComponent
      attachment="top left"
      style={{ zIndex: 99999999999 }}
      offset={collapsed ? "68px -14px" : "72px -18px"}
      renderTarget={(ref) => {
        return (
          <div ref={displayRef} className="bg-nav-shell flex justify-center">
            <EnvironmentDisplay
              ref={ref as React.Ref<HTMLButtonElement>}
              collapsed={collapsed}
              onClick={toggleOpen}
              open={open}
              className={className}
            />
          </div>
        );
      }}
      renderElement={(ref) => {
        return (
          <>
            {open && (
              <div ref={panelRef}>
                <EnvironmentPickerPanel
                  setEnvironmentAndProject={setEnvironmentAndProjectFromPanel}
                  projects={projects}
                  onClose={handleClose}
                  ref={ref as React.Ref<HTMLDivElement>}
                />
              </div>
            )}
          </>
        );
      }}
    />
  );
};
